import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import api from '@/services/api';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import Card from '@/components/ui/Card';
import { User, Mail, Lock, AlertCircle, CheckCircle, RefreshCw, Copy, Eye, EyeOff } from 'lucide-react';
import { generateUserPassword, copyToClipboard, checkPasswordStrength } from '@/utils/passwordGenerator';
import { parseErrorMessage } from '@/utils/errorHandler';

// 密码强度指示器组件
const PasswordStrengthIndicator: React.FC<{ password: string }> = ({ password }) => {
  const strength = checkPasswordStrength(password);
  
  const colors = {
    weak: 'bg-red-500',
    fair: 'bg-yellow-500',
    good: 'bg-blue-500',
    strong: 'bg-green-500'
  };
  
  const textColors = {
    weak: 'text-red-600',
    fair: 'text-yellow-600',
    good: 'text-blue-600',
    strong: 'text-green-600'
  };
  
  const levelLabels = {
    weak: '弱',
    fair: '一般',
    good: '良好',
    strong: '强'
  };
  
  return (
    <div className="mt-2">
      <div className="space-y-1">
        <div className="flex justify-between text-xs">
          <span className={textColors[strength.level]}>
            密码强度: {levelLabels[strength.level]}
          </span>
          <span className="text-gray-500">{strength.score}/100</span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
          <div 
            className={`h-2 rounded-full transition-all duration-300 ${colors[strength.level]}`}
            style={{ width: `${strength.score}%` }}
          ></div>
        </div>
        {strength.feedback.length > 0 && (
          <div className="text-xs text-gray-500 mt-1">
            提示: {strength.feedback.join('、')}
          </div>
        )}
      </div>
    </div>
  );
};

const Register: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordMessage, setPasswordMessage] = useState('');
  const [formData, setFormData] = useState({
    username: '',
    real_name: '',
    email: '',
    password: '',
    confirmPassword: '',
  });
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    // 验证密码一致性
    if (formData.password !== formData.confirmPassword) {
      setError('两次输入的密码不一致');
      setLoading(false);
      return;
    }

    // 验证密码强度
    const passwordStrength = checkPasswordStrength(formData.password);
    if (passwordStrength.level === 'weak') {
      setError('密码强度太弱，请使用更强的密码');
      setLoading(false);
      return;
    }
    
    // 对于"一般"强度的密码，给出警告但允许提交
    // 后端会进行最终验证（至少8位，包含字母和数字）

    try {
      await api.register({
        username: formData.username,
        real_name: formData.real_name,
        email: formData.email,
        password: formData.password,
      });
      
      setSuccess(true);
    } catch (error: any) {
      setError(parseErrorMessage(error, '注册失败，请重试'));
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const generateAndSetPassword = async () => {
    const newPassword = generateUserPassword();
    setFormData(prev => ({
      ...prev,
      password: newPassword,
      confirmPassword: newPassword,
    }));
    
    // 自动复制到剪贴板
    const success = await copyToClipboard(newPassword);
    if (success) {
      setPasswordMessage('强密码已生成并复制到剪贴板！');
      setTimeout(() => setPasswordMessage(''), 3000);
    } else {
      setPasswordMessage('强密码已生成，请手动复制');
      setTimeout(() => setPasswordMessage(''), 3000);
    }
  };

  const copyPasswordToClipboard = async () => {
    if (formData.password) {
      const success = await copyToClipboard(formData.password);
      if (success) {
        setPasswordMessage('密码已复制到剪贴板！');
        setTimeout(() => setPasswordMessage(''), 2000);
      } else {
        setPasswordMessage('复制失败，请手动复制');
        setTimeout(() => setPasswordMessage(''), 3000);
      }
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary-500 via-primary-600 to-primary-700 flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <Card className="shadow-elevated">
            <Card.Header className="text-center pb-6">
              <div className="mx-auto w-12 h-12 bg-green-600 rounded-full flex items-center justify-center mb-4">
                <CheckCircle className="h-6 w-6 text-white" />
              </div>
              <Card.Title className="text-2xl font-bold text-gray-900">
                注册成功
              </Card.Title>
            </Card.Header>

            <Card.Content className="text-center">
              <p className="text-gray-600 mb-6">
                您的账户已提交注册申请，请等待管理员审批。
              </p>
              <p className="text-sm text-gray-500 mb-6">
                审批通过后，您将可以使用注册的用户名和密码登录系统。
              </p>
              <Button 
                onClick={() => navigate('/login')} 
                className="w-full"
              >
                返回登录
              </Button>
            </Card.Content>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-500 via-primary-600 to-primary-700 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <Card className="shadow-elevated">
          <Card.Header className="text-center pb-6">
            <div className="mx-auto w-12 h-12 bg-primary-600 rounded-full flex items-center justify-center mb-4">
              <User className="h-6 w-6 text-white" />
            </div>
            <Card.Title className="text-2xl font-bold text-gray-900">
              用户注册
            </Card.Title>
            <p className="text-gray-600 mt-2">创建您的账户</p>
          </Card.Header>

          <Card.Content>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-4">
                <div className="relative">
                  <Input
                    name="username"
                    type="text"
                    label="用户名"
                    value={formData.username}
                    onChange={handleInputChange}
                    required
                    className="pl-10"
                    placeholder="请输入用户名"
                  />
                  <User className="absolute left-3 top-9 h-4 w-4 text-gray-400" />
                </div>

                <div className="relative">
                  <Input
                    name="real_name"
                    type="text"
                    label="真实姓名"
                    value={formData.real_name}
                    onChange={handleInputChange}
                    required
                    className="pl-10"
                    placeholder="请输入真实姓名"
                  />
                  <User className="absolute left-3 top-9 h-4 w-4 text-gray-400" />
                </div>

                <div className="relative">
                  <Input
                    name="email"
                    type="email"
                    label="邮箱"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className="pl-10"
                    placeholder="请输入邮箱地址"
                  />
                  <Mail className="absolute left-3 top-9 h-4 w-4 text-gray-400" />
                </div>

                {/* 密码输入区域 */}
                <div className="space-y-2">
                  <div className="relative">
                    <Input
                      name="password"
                      type={showPassword ? "text" : "password"}
                      label="密码"
                      value={formData.password}
                      onChange={handleInputChange}
                      required
                      className="pl-10 pr-24"
                      placeholder="请输入密码"
                      autoComplete="new-password"
                    />
                    <Lock className="absolute left-3 top-9 h-4 w-4 text-gray-400" />
                    <div className="absolute right-2 top-8 flex space-x-1">
                      <Button
                        size="sm"
                        type="default"
                        onClick={() => setShowPassword(!showPassword)}
                        className="p-1 h-7 w-7"
                        title={showPassword ? "隐藏密码" : "显示密码"}
                      >
                        {showPassword ? <EyeOff className="h-3 w-3" /> : <Eye className="h-3 w-3" />}
                      </Button>
                      <Button
                        size="sm"
                        type="default"
                        onClick={copyPasswordToClipboard}
                        disabled={!formData.password}
                        className="p-1 h-7 w-7"
                        title="复制密码"
                      >
                        <Copy className="h-3 w-3" />
                      </Button>
                    </div>
                  </div>
                  
                  {/* 密码生成按钮 */}
                  <Button
                    type="default"
                    onClick={generateAndSetPassword}
                    className="w-full flex items-center justify-center"
                    size="sm"
                  >
                    <RefreshCw className="h-4 w-4 mr-2" />
                    生成强密码
                  </Button>
                  
                  {/* 密码强度指示器 */}
                  {formData.password && (
                    <PasswordStrengthIndicator password={formData.password} />
                  )}
                  
                  {/* 密码消息提示 */}
                  {passwordMessage && (
                    <div className="flex items-center space-x-2 text-green-600 bg-green-50 p-2 rounded-md text-sm">
                      <CheckCircle className="h-4 w-4 flex-shrink-0" />
                      <span>{passwordMessage}</span>
                    </div>
                  )}
                </div>

                <div className="space-y-1">
                  <div className="relative">
                    <Input
                      name="confirmPassword"
                      type={showConfirmPassword ? "text" : "password"}
                      label="确认密码"
                      value={formData.confirmPassword}
                      onChange={handleInputChange}
                      required
                      className="pl-10 pr-12"
                      placeholder="请再次输入密码"
                      autoComplete="new-password"
                    />
                    <Lock className="absolute left-3 top-9 h-4 w-4 text-gray-400" />
                    <Button
                      size="sm"
                      type="default"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute right-2 top-8 p-1 h-7 w-7"
                      title={showConfirmPassword ? "隐藏密码" : "显示密码"}
                    >
                      {showConfirmPassword ? <EyeOff className="h-3 w-3" /> : <Eye className="h-3 w-3" />}
                    </Button>
                  </div>
                  
                  {/* 密码一致性提示 */}
                  {formData.confirmPassword && (
                    <div className="text-xs">
                      {formData.password === formData.confirmPassword ? (
                        <span className="text-green-600 flex items-center">
                          <CheckCircle className="h-3 w-3 mr-1" />
                          密码一致
                        </span>
                      ) : (
                        <span className="text-red-600 flex items-center">
                          <AlertCircle className="h-3 w-3 mr-1" />
                          密码不一致
                        </span>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {error && (
                <div className="flex items-center space-x-2 text-red-600 bg-red-50 p-3 rounded-md">
                  <AlertCircle className="h-4 w-4 flex-shrink-0" />
                  <span className="text-sm">{error}</span>
                </div>
              )}

              <Button
                type="submit"
                loading={loading}
                className="w-full"
                size="lg"
              >
                注册
              </Button>
            </form>

            <div className="mt-6 text-center">
              <p className="text-sm text-gray-600">
                已有账户？{' '}
                <Link to="/login" className="text-primary-600 hover:text-primary-500 font-medium">
                  立即登录
                </Link>
              </p>
            </div>

            <div className="mt-6 p-4 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-600 text-center font-medium mb-2">
                注册须知
              </p>
              <div className="text-xs text-gray-500 space-y-1">
                <p>• 注册后需要等待管理员审批</p>
                <p>• 审批通过后才能正常登录使用</p>
                <p>• 请确保填写的信息真实有效</p>
                <p>• 建议使用"生成强密码"功能创建安全密码</p>
                <p>• 密码将自动复制到剪贴板，请妥善保存</p>
              </div>
            </div>
          </Card.Content>
        </Card>
      </div>
    </div>
  );
};

export default Register;